import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'

export interface AgeBucket {
  label: string
  min_age: number
  max_age: number | null
  count: number
}

export interface SeriesStats {
  avg_age: number | null
  median_age: number | null
  p25_age: number | null
  p75_age: number | null
}

export interface AlderSeries {
  merke: string
  klasse: string | null
  total: number
  buckets: AgeBucket[]
  stats: SeriesStats
}

export interface AlderResponse {
  series: AlderSeries[]
}

/**
 * Display label for one chart/grid series. A series is identified by
 * (merke, klasse): merke is "Alle" when no brand filter is active, klasse is
 * null when the klasse split is off. Mirrors MerkeModell/PerUkeChart's
 * seriesLabel idea.
 */
export function seriesLabel(merke: string, klasse: string | null): string {
  if (klasse && merke !== 'Alle') return `${merke} – ${klasse}`
  if (klasse) return klasse
  return merke
}

type BaseParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }

function buildQS(filters: BaseParams, merker: string[]): string {
  const qs = new URLSearchParams()
  qs.set('from_date', filters.from)
  qs.set('to_date', filters.to)
  for (const k of filters.klasse) qs.append('klasse', k)
  // Omit varebil for "alle" (undefined); false = personbil, true = varebil.
  if (filters.varebil !== undefined) qs.set('varebil', String(filters.varebil))
  // Omit omregistrering when false/undefined — only send true when the filter is active.
  if (filters.omregistrering) qs.set('omregistrering', 'true')
  if (filters.selgerPostnummer) for (const p of filters.selgerPostnummer.split(',')) qs.append('selger_postnummer', p.trim())
  if (filters.kjoperPostnummer) for (const p of filters.kjoperPostnummer.split(',')) qs.append('kjoper_postnummer', p.trim())
  for (const m of merker) qs.append('merke', m)
  return qs.toString()
}

export function fetchBilAlder(filters: BaseParams, merker: string[] = []): Promise<{ data: AlderResponse }> {
  return api.get(`/bruktbil/aldersprofil/bil-alder?${buildQS(filters, merker)}`)
}

export function fetchKjoperAlder(filters: BaseParams, merker: string[] = []): Promise<{ data: AlderResponse }> {
  return api.get(`/bruktbil/aldersprofil/kjoper-alder?${buildQS(filters, merker)}`)
}

export interface MerkeOption { merke: string | null; count: number }

export function fetchMerkeOptions(
  filters: BaseParams,
  limit = 250,
): Promise<{ data: { rows: MerkeOption[] } }> {
  const qs = new URLSearchParams()
  qs.set('from_date', filters.from)
  qs.set('to_date', filters.to)
  for (const k of filters.klasse) qs.append('klasse', k)
  // Omit varebil for "alle" (undefined); false = personbil, true = varebil.
  if (filters.varebil !== undefined) qs.set('varebil', String(filters.varebil))
  // Omit omregistrering when false/undefined — only send true when the filter is active.
  if (filters.omregistrering) qs.set('omregistrering', 'true')
  if (filters.selgerPostnummer) for (const p of filters.selgerPostnummer.split(',')) qs.append('selger_postnummer', p.trim())
  if (filters.kjoperPostnummer) for (const p of filters.kjoperPostnummer.split(',')) qs.append('kjoper_postnummer', p.trim())
  qs.set('limit', String(limit))
  return api.get(`/bruktbil/merke-modell/merke?${qs.toString()}`)
}
